import { useMediaQuery } from '@material-ui/core'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'
import { useAtom } from 'jotai'
import { useRouter } from 'next/router'
import * as React from 'react'
import styled from 'styled-components'
import { LeaderboardTime } from '..'
import { rwSolPriceAtom } from '../../../atoms/WalletName'
import { VideoFullData } from '../../../interfaces'
import { VideoSelectedFilter } from '../TrendingVideos'

const StyledRow = styled.tr`
  width: 100%;
  border: none;
  border-spacing: 0;

  td {
    background: linear-gradient(rgb(45, 45, 45), rgb(20, 20, 20));
    border-color: #323232;
    border-width: 1px;
    border-style: solid;
    border-left: none;
    border-right: none;
    color: white;
    font-size: 24px;
    font-weight: 700;
    white-space: nowrap;

    @media (max-width: 62em) {
      text-align: right;
      padding-right: 15px;
    }

    @media (max-width: 43em) {
      font-size: 18px;
    }
  }

  td:first-child {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
    border-left: 1px solid #323232;
  }

  td:last-child {
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
    border-right: 1px solid #323232;
  }
`

const VideoThumbnailContainer = styled.div`
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  border-radius: 5px;
  cursor: pointer;
  margin-bottom: 7.5px;

  @media (min-width: 62em) {
    border-radius: 5px;
  }
`

const VideoThumbnail = styled.div<{ poster: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 5px;
  background-color: black;
  cursor: pointer;
  background-image: url(${(props) => props.poster});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;

  @media (min-width: 62em) {
    border-radius: 5px;
  }
`

const VideoOuterDiv = styled.div`
  width: 110px;
`

const VideoCell = styled.div`
  padding: 15px 0;
  padding-left: 15px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 25px;
  cursor: pointer;

  @media (max-width: 43em) {
    gap: 15px;
  }
`

const Rank = styled.div`
  color: white;
  font-size: 18px;
  font-weight: 700;
  text-align: left;
  width: 25px;

  @media (max-width: 43em) {
    font-size: 14px;
  }
`

const VideoTitle = styled.div`
  color: white;
  font-size: 18px;
  font-weight: 700;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 175px;

  @media (max-width: 43em) {
    font-size: 14px;
  }

  @media (min-width: 43em, max-width: 63em) {
    max-width: 200px;
  }
`

const ValueCell = styled.div<{ left?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 5px;

  @media (max-width: 62em) {
    align-items: ${(props) => (props.left ? 'flex-start' : 'flex-end')};
  }
`

const Subtitle = styled.div`
  color: #aaa;
  font-weight: 500;
  font-size: 14px;

  @media (max-width: 43em) {
    font-size: 12px;
  }
`

const VideoRow = ({
  video,
  creator,
  leaderboardInfo,
  rank,
  time,
  filter,
}: VideoFullData & {
  rank: number
  time: LeaderboardTime
  filter: VideoSelectedFilter
}): JSX.Element => {
  const [price, setPrice] = useAtom(rwSolPriceAtom)
  const isTablet = useMediaQuery('(max-width: 62em)')
  const [userHover, setUserHover] = React.useState(false)
  const router = useRouter()

  const getSolPrice = async (): Promise<void> => {
    const resp = await fetch(
      'https://api.coingecko.com/api/v3/simple/price?vs_currencies=usd&ids=solana'
    )
    const json = await resp.json()
    setPrice(json['solana']['usd'] as number)
  }

  React.useEffect(() => {
    if (price === 0) {
      getSolPrice()
    }
  }, [])

  const hoverStyle = {
    textDecoration: userHover ? 'underline' : 'none',
  }

  const handleClick = (e: React.MouseEvent): void => {
    e.stopPropagation()
    router.push(`/watch/${video.address}`)
  }

  const primary =
    (time === LeaderboardTime.ALL
      ? leaderboardInfo?.allTimePrim
      : time === LeaderboardTime.MONTH
      ? leaderboardInfo?.thirtyDayPrim
      : leaderboardInfo?.sevenDayPrim) ?? 0

  const secondary =
    (time === LeaderboardTime.ALL
      ? leaderboardInfo?.allTimeSec
      : time === LeaderboardTime.MONTH
      ? leaderboardInfo?.thirtyDaySec
      : leaderboardInfo?.sevenDaySec) ?? 0

  const total =
    (time === LeaderboardTime.ALL
      ? leaderboardInfo?.allTime
      : time === LeaderboardTime.MONTH
      ? leaderboardInfo?.thirtyDays
      : leaderboardInfo?.sevenDays) ?? 0

  if (isTablet) {
    return (
      <StyledRow>
        <td>
          <VideoCell
            onClick={handleClick}
            onMouseEnter={() => setUserHover(true)}
            onMouseLeave={() => setUserHover(false)}
          >
            <Rank>{'#' + rank}</Rank>
            {/* <VideoOuterDiv>
              <VideoThumbnailContainer>
                <VideoThumbnail
                  poster={`https://glassimg.imgix.net/${video.thumbnail_uri}?ar=16:9&fit=crop&fm=webp&w=400&h=225`}
                />
              </VideoThumbnailContainer>
            </VideoOuterDiv> */}
            <ValueCell left>
              <VideoTitle style={hoverStyle}>{video.title}</VideoTitle>
              <Subtitle>{creator?.displayName}</Subtitle>
            </ValueCell>
          </VideoCell>
        </td>
        {filter === VideoSelectedFilter.PRIMARY ? (
          <td>
            <ValueCell>
              {Math.round((primary / LAMPORTS_PER_SOL) * 100) / 100} SOL
              <Subtitle>${((primary / LAMPORTS_PER_SOL) * price).toFixed(2)}</Subtitle>
            </ValueCell>
          </td>
        ) : filter === VideoSelectedFilter.SECONDARY ? (
          <td>
            <ValueCell>
              {Math.round((secondary / LAMPORTS_PER_SOL) * 100) / 100} SOL
              <Subtitle>${((secondary / LAMPORTS_PER_SOL) * price).toFixed(2)}</Subtitle>
            </ValueCell>
          </td>
        ) : filter === VideoSelectedFilter.TOTAL ? (
          <td>
            <ValueCell>
              {Math.round((total / LAMPORTS_PER_SOL) * 100) / 100} SOL
              <Subtitle>${((total / LAMPORTS_PER_SOL) * price).toFixed(2)}</Subtitle>
            </ValueCell>
          </td>
        ) : null}
      </StyledRow>
    )
  }

  return (
    <StyledRow>
      <td>
        <VideoCell
          onClick={handleClick}
          onMouseEnter={() => setUserHover(true)}
          onMouseLeave={() => setUserHover(false)}
        >
          <Rank>{'#' + rank}</Rank>
          <VideoOuterDiv>
            <VideoThumbnailContainer>
              <VideoThumbnail
                poster={`https://glassimg.imgix.net/${video.thumbnail_uri}?ar=16:9&fit=crop&fm=webp&w=400&h=225`}
              />
            </VideoThumbnailContainer>
          </VideoOuterDiv>
          <ValueCell>
            <VideoTitle style={hoverStyle}>{video.title}</VideoTitle>
            <Subtitle>{creator?.displayName}</Subtitle>
          </ValueCell>
        </VideoCell>
      </td>
      <td>
        <ValueCell>
          {Math.round((primary / LAMPORTS_PER_SOL) * 100) / 100} SOL
          <Subtitle>${((primary / LAMPORTS_PER_SOL) * price).toFixed(2)}</Subtitle>
        </ValueCell>
      </td>
      <td>
        <ValueCell>
          {Math.round((secondary / LAMPORTS_PER_SOL) * 100) / 100} SOL
          <Subtitle>${((secondary / LAMPORTS_PER_SOL) * price).toFixed(2)}</Subtitle>
        </ValueCell>
      </td>
      <td>
        <ValueCell>
          {Math.round((total / LAMPORTS_PER_SOL) * 100) / 100} SOL
          <Subtitle>${((total / LAMPORTS_PER_SOL) * price).toFixed(2)}</Subtitle>
        </ValueCell>
      </td>
    </StyledRow>
  )
}

export default VideoRow
